import { unicodes } from './unicodes.js'
import { splitTextInArray } from './array.js'
import {
  formatBoldItalicLetterToNormal,
  formatNormalLetterToBoldItalic,
  matchIsTextIsBoldItalic,
  matchIsUnicodeBoldItalic
} from './bold-italic.js'
import {
  formatItalicLetterToNormal,
  formatNormalLetterToItalic,
  matchIsUnicodeItalic
} from './italic.js'
import { matchIsNumber } from './number.js'
import {
  getUnicodeLetter,
  matchIsCharacterANormalLetter,
  matchIsTextEmpty,
  matchIsTextUnknown,
  matchIsTextUppercase
} from './string.js'

export function matchIsUnicodeLowerBold(unicode) {
  return (
    (unicode >= unicodes.bold.a && unicode <= unicodes.bold.z) ||
    (unicode >= unicodes.legacyBold.a && unicode <= unicodes.legacyBold.z)
  )
}

export function matchIsUnicodeUpperBold(unicode) {
  return (
    (unicode >= unicodes.bold.A && unicode <= unicodes.bold.Z) ||
    (unicode >= unicodes.legacyBold.A && unicode <= unicodes.legacyBold.Z)
  )
}

export function matchIsUnicodeBold(unicode) {
  return matchIsUnicodeLowerBold(unicode) || matchIsUnicodeUpperBold(unicode)
}

function matchIsLetterBold(letter) {
  const unicode = getUnicodeLetter(letter)
  if (!matchIsNumber(unicode)) {
    return true
  }
  if (matchIsUnicodeItalic(unicode)) {
    return false
  }
  return (
    matchIsUnicodeBoldItalic(unicode) ||
    matchIsUnicodeBold(unicode) ||
    !matchIsCharacterANormalLetter(letter)
  )
}

export function matchIsTextIsBold(text) {
  if (matchIsTextEmpty(text) || matchIsTextUnknown(text)) {
    return false
  }
  const textSplitted = splitTextInArray(text)
  return textSplitted.every(matchIsLetterBold)
}

export function formatNormalLetterToBold(normalLetter) {
  const unicode = getUnicodeLetter(normalLetter)
  if (!matchIsNumber(unicode) || !matchIsCharacterANormalLetter(normalLetter)) {
    return normalLetter
  }
  if (matchIsTextUppercase(normalLetter)) {
    return String.fromCodePoint(unicodes.bold.A - unicodes.normal.A + unicode)
  }
  return String.fromCodePoint(unicodes.bold.a - unicodes.normal.a + unicode)
}

export function formatBoldLetterToNormal(boldLetter) {
  const unicode = getUnicodeLetter(boldLetter)
  if (!matchIsNumber(unicode) || !matchIsUnicodeBold(unicode)) {
    return boldLetter
  }
  // Normalize from whichever bold block the glyph belongs to (current sans-serif or legacy serif).
  if (unicode >= unicodes.legacyBold.A && unicode <= unicodes.legacyBold.Z) {
    return String.fromCodePoint(
      unicodes.normal.A - unicodes.legacyBold.A + unicode
    )
  }
  if (unicode >= unicodes.legacyBold.a && unicode <= unicodes.legacyBold.z) {
    return String.fromCodePoint(
      unicodes.normal.a - unicodes.legacyBold.a + unicode
    )
  }
  if (matchIsUnicodeUpperBold(unicode)) {
    return String.fromCodePoint(unicodes.normal.A - unicodes.bold.A + unicode)
  }
  return String.fromCodePoint(unicodes.normal.a - unicodes.bold.a + unicode)
}

export function removeBoldFromText(text) {
  const textSplitted = splitTextInArray(text)
  const textSplittedFormatted = textSplitted.map((letter) => {
    if (matchIsTextIsBoldItalic(letter)) {
      const normalLetter = formatBoldItalicLetterToNormal(letter)
      return formatNormalLetterToItalic(normalLetter)
    }
    if (matchIsTextIsBold(letter)) {
      return formatBoldLetterToNormal(letter)
    }
    return letter
  })
  return textSplittedFormatted.join('')
}

export function formatBold(text) {
  const textSplitted = splitTextInArray(text)
  const textSplittedFormatted = textSplitted.map((letter) => {
    const unicode = getUnicodeLetter(letter)
    if (!matchIsNumber(unicode)) {
      return letter
    }
    if (matchIsUnicodeItalic(unicode)) {
      return formatNormalLetterToBoldItalic(formatItalicLetterToNormal(letter))
    }
    if (matchIsUnicodeBold(unicode)) {
      return formatNormalLetterToBold(formatBoldLetterToNormal(letter))
    }
    return formatNormalLetterToBold(letter)
  })
  return textSplittedFormatted.join('')
}
